import * as THREE from "three";
import { Reflector } from "three/addons/objects/Reflector.js";

export default class TexturedReflector extends Reflector {
    constructor(geometry, options = {}) {
        const { map, ...reflectorOptions } = options;

        const texturedShader = {
            name: "TexturedReflectorShader",
            uniforms: {
                color: { value: null },
                tDiffuse: { value: null },
                tBase: { value: null },
                textureMatrix: { value: null },
                reflectivity: { value: 0.5 },
                exposure: { value: 2.0 },
            },
            vertexShader: /* glsl */ `
                uniform mat4 textureMatrix;
                varying vec4 vUv;
                varying vec2 vBaseUv;
                #include <common>
                #include <logdepthbuf_pars_vertex>
                void main() {
                    vUv = textureMatrix * vec4( position, 1.0 );
                    vBaseUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                    #include <logdepthbuf_vertex>
                }
            `,
            fragmentShader: /* glsl */ `
                uniform vec3 color;
                uniform float reflectivity;
                uniform float exposure;
                uniform sampler2D tDiffuse;
                uniform sampler2D tBase;
                varying vec4 vUv;
                varying vec2 vBaseUv;
                #include <logdepthbuf_fragment>
                float blendOverlay( float base, float blend ) {
                    return ( base < 0.5 ? ( 2.0 * base * blend ) : ( 1.0 - 2.0 * ( 1.0 - base ) * ( 1.0 - blend ) ) );
                }
                vec3 blendOverlay( vec3 base, vec3 blend ) {
                    return vec3( blendOverlay( base.r, blend.r ), blendOverlay( base.g, blend.g ), blendOverlay( base.b, blend.b ) );
                }
                void main() {
                    #include <logdepthbuf_fragment>
                    vec4 reflection = texture2DProj( tDiffuse, vUv );
                    vec4 baseColor = texture2D( tBase, vBaseUv );
                    
                    vec3 finalColor = baseColor.rgb * color;
                    // tonemap the finalColor to make it whiter
                    finalColor = 1.0 - exp(-finalColor * exposure);

                    vec3 blended = blendOverlay( reflection.rgb, finalColor );
                    
                    gl_FragColor = vec4( mix( finalColor, blended, reflectivity ), 1.0 );
                    
                    #include <tonemapping_fragment>
                    #include <colorspace_fragment>
                }
            `,
        };

        // Initialize Reflector with custom shader
        super(geometry, { ...reflectorOptions, shader: texturedShader });

        const scope = this;

        // Set the map uniform if provided
        if (map) {
            this.material.uniforms["tBase"].value = map;
            this.material.uniforms["tBase"].value.needsUpdate = true;
        }

        // Add color uniform management
        if (reflectorOptions.color) {
            this.material.uniforms["color"].value = new THREE.Color(
                reflectorOptions.color,
            );
        }

        if (options.reflectivity !== undefined) {
            this.material.uniforms["reflectivity"].value = options.reflectivity;
        }
        if (options.exposure !== undefined) {
            this.material.uniforms["exposure"].value = options.exposure;
        }
    }

    get map() {
        return this.material.uniforms["tBase"].value;
    }

    set map(value) {
        this.material.uniforms["tBase"].value = value;
    }

    get reflectivity() {
        return this.material.uniforms["reflectivity"].value;
    }

    set reflectivity(value) {
        this.material.uniforms["reflectivity"].value = value;
    }

    get exposure() {
        return this.material.uniforms["exposure"].value;
    }

    set exposure(value) {
        this.material.uniforms["exposure"].value = value;
    }
}
